import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SlidersHorizontal, Loader2, Sparkles, ChevronDown, ChevronRight, Lock, Boxes, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MODULE_ROUTES } from "@/lib/features";
import { SECTOR_GATED_ROUTES, sectorIcon, groupSectors } from "@/lib/sectors";

const GATED = new Set(SECTOR_GATED_ROUTES);

// Sektör paketi seçilince otomatik uygulanacak GERÇEK iş kuralı (cap/behavior) önerileri.
// Sektör koduna göre; boşsa yalnız modül bayrakları uygulanır. İşletme tipine en mantıklı kurallar tek tıkla.
const SECTOR_RULE_DEFAULTS = {
  toptan:        { "behavior.require_customer_on_sale": true, "behavior.require_open_shift": true, "cap.coupon": false, "cap.meal_card": false },
  depo:          { "behavior.require_customer_on_sale": true, "cap.coupon": false, "cap.meal_card": false },
  dis_ticaret:   { "behavior.require_customer_on_sale": true, "cap.coupon": false, "cap.meal_card": false },
  kafe:          { "cap.meal_card": true, "behavior.require_open_shift": true, "cap.coupon": true },
  otel:          { "cap.meal_card": true, "behavior.require_open_shift": true },
  firin_pastane: { "cap.meal_card": true, "behavior.require_open_shift": true },
  saglik:        { "behavior.require_customer_on_sale": true },
  market:        { "cap.coupon": true, "cap.meal_card": true, "behavior.require_open_shift": true },
  magaza:        { "cap.coupon": true, "behavior.require_open_shift": true },
  eticaret:      { "cap.coupon": true, "cap.meal_card": false, "behavior.require_customer_on_sale": true },
  kasap:         { "cap.coupon": true, "cap.meal_card": true, "behavior.require_open_shift": true },
  manav:         { "cap.coupon": true, "cap.meal_card": true, "behavior.require_open_shift": true },
  gida:          { "cap.coupon": true, "cap.meal_card": true, "behavior.require_open_shift": true },
  su_tup:        { "cap.meal_card": false, "behavior.require_customer_on_sale": true },
  akaryakit:     { "cap.meal_card": true, "behavior.require_open_shift": true },
  eczane:        { "behavior.require_customer_on_sale": true, "cap.coupon": false, "cap.meal_card": false },
  optik:         { "behavior.require_customer_on_sale": true, "cap.coupon": true },
  kuyumcu:       { "behavior.require_customer_on_sale": true, "cap.meal_card": false },
  giyim:         { "cap.coupon": true, "behavior.require_open_shift": true },
  ayakkabi_canta:{ "cap.coupon": true, "behavior.require_open_shift": true },
  kirtasiye:     { "cap.coupon": true, "behavior.require_open_shift": true },
  yapi_market:   { "cap.coupon": true, "behavior.require_open_shift": true },
  mobilya:       { "behavior.require_customer_on_sale": true },
  elektronik:    { "cap.coupon": true, "behavior.require_customer_on_sale": true },
  otomotiv:      { "behavior.require_customer_on_sale": true },
  kuafor:        { "cap.meal_card": true, "behavior.require_open_shift": true },
  spor:          { "cap.coupon": true, "behavior.require_open_shift": true },
  veteriner:     { "behavior.require_customer_on_sale": true },
  uretim:        { "behavior.require_customer_on_sale": true, "cap.coupon": false, "cap.meal_card": false },
  insaat:        { "behavior.require_customer_on_sale": true, "cap.coupon": false, "cap.meal_card": false },
  reklam:        { "behavior.require_customer_on_sale": true, "cap.meal_card": false },
  yazilim:       { "behavior.require_customer_on_sale": true, "cap.meal_card": false },
  tarim:         { "behavior.require_customer_on_sale": true },
  emlak:         { "behavior.require_customer_on_sale": true, "cap.meal_card": false },
  teknik_servis: { "behavior.require_customer_on_sale": true },
  egitim:        { "behavior.require_customer_on_sale": true, "cap.meal_card": false },
  turizm:        { "behavior.require_customer_on_sale": true },
};

// Çekirdek (her zaman açık) modüller — bilgi amaçlı gösterilir, kapatılamaz.
const CORE_MODULES = [
  "Panel", "Ürün Kartları", "Fiyat Yönetimi", "Stok", "Kasa / Hızlı Satış",
  "Satışlar", "Vardiyalar (Z Rapor)", "Müşteriler", "Raporlar", "Mesajlar",
  "Kullanıcılar", "Roller & Yetkiler", "Lokasyonlar", "Sistem Ayarları",
];

// Bir sektörün modül listesinden eklenti-modül bayrağı override'ı üretir (Sektör → Modül).
function sectorOverrides(sector) {
  const ov = {};
  Object.entries(MODULE_ROUTES).forEach(([flag, routes]) => {
    const gated = routes.filter((r) => GATED.has(r));
    if (gated.length === 0) return; // AI/Mesajlar gibi çekirdek/araçlara dokunma
    ov[flag] = gated.some((r) => (sector.modules || []).includes(r));
  });
  return ov;
}

export default function BusinessFeaturesSettings() {
  const { refreshFeatures } = useAuth();
  const [groups, setGroups] = useState([]);
  const [eff, setEff] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [openPkg, setOpenPkg] = useState({});
  const [sectors, setSectors] = useState([]);
  const [pkgQuery, setPkgQuery] = useState("");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/features/catalog");
      setGroups(r.data.groups || []);
      setEff(r.data.effective || {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/sectors").then((r) => setSectors((r.data || []).filter((s) => s.is_active !== false))).catch(() => {});
  }, []);

  const toggle = async (key, val) => {
    setEff((s) => ({ ...s, [key]: val }));
    setSavingKey(key);
    try {
      const r = await api.put("/settings/features", { features: { [key]: val } });
      setEff(r.data.effective || {});
      await refreshFeatures?.();
      toast.success("Kaydedildi");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setEff((s) => ({ ...s, [key]: !val }));
    } finally { setSavingKey(null); }
  };

  const labelOf = (k) => { for (const g of groups) for (const f of (g.flags || [])) if (f.key === k) return f.label; return k; };

  // Paketi UYGULAMADAN önce önizleme aç — hangi POS kuralları açılıp kapanacak, kullanıcıya göster.
  const openPreview = (sector) => {
    const isFull = sector.code === "full";
    let ov;
    if (isFull) { ov = {}; groups.forEach((g) => g.flags.forEach((f) => { ov[f.key] = true; })); }
    else ov = { ...sectorOverrides(sector), ...(SECTOR_RULE_DEFAULTS[sector.code] || {}) };
    const ruleKeys = Object.keys(SECTOR_RULE_DEFAULTS[sector.code] || {});
    const rules = isFull ? [] : ruleKeys.map((k) => ({ key: k, label: labelOf(k), to: !!ov[k], changed: (eff[k] === true) !== !!ov[k] }));
    const modCount = (sector.modules || []).filter((r) => GATED.has(r)).length;
    setPreview({ sector, ov, isFull, rules, modCount });
  };

  const confirmApply = async () => {
    if (!preview) return;
    const { sector, ov } = preview;
    setSavingKey(`pkg:${sector.code}`);
    try {
      const r = await api.put("/settings/features", { features: ov });
      setEff(r.data.effective || {});
      await refreshFeatures?.();
      toast.success(`"${sector.name}" paketi uygulandı`);
      setPreview(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingKey(null); }
  };

  if (loading) return <p className="text-sm text-slate-400" data-testid="features-loading"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Yükleniyor…</p>;

  const t = pkgQuery.trim().toLocaleLowerCase("tr");
  const filteredSectors = sectors.filter((s) => !t || (s.name || "").toLocaleLowerCase("tr").includes(t));
  const pkgGroups = groupSectors(filteredSectors);
  const isPkgOpen = (g, i) => (t ? true : (g in openPkg ? openPkg[g] : (g === "⭐ Özel" || i === 0)));

  return (
    <div data-testid="business-features-settings" className="space-y-5">
      <div>
        <p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><SlidersHorizontal className="h-5 w-5 text-brand-green" /> Modüller & İş Akışı</p>
        <p className="mt-1 text-sm text-slate-500"><b>Çekirdek + Eklenti + Paket</b> mimarisi: Çekirdek modüller her zaman açıktır. Bir <b>sektör paketiyle</b> tek tıkla kurun (★ Tümü = her şey açık); ardından aşağıdaki gruplardan yetenek, zorunlu alan ve iş akışı adımlarını tek tek ayarlayın. Değişiklikler <b>anında</b> kaydedilir.</p>
      </div>

      {/* Çekirdek modüller — bilgi */}
      <div data-testid="core-modules-card" className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
        <p className="flex items-center gap-1.5 font-heading text-sm font-bold text-slate-800"><Lock className="h-4 w-4 text-slate-400" /> Çekirdek Modüller (her zaman açık)</p>
        <p className="mb-2 mt-0.5 text-[11px] text-slate-400">Bu modüller sistemin omurgasıdır; kapatılamaz. İşletmenizin türü ne olursa olsun her zaman kullanılabilir.</p>
        <div className="flex flex-wrap gap-1.5">
          {CORE_MODULES.map((m) => (
            <span key={m} className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-500 ring-1 ring-inset ring-slate-200"><Boxes className="h-3 w-3 text-slate-400" /> {m}</span>
          ))}
        </div>
      </div>

      {/* Sektör paketleri — gruplu */}
      <div data-testid="sector-packages" className="rounded-xl border border-brand-green/20 bg-brand-green/[0.04] p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="flex items-center gap-1.5 font-heading text-sm font-bold text-slate-800"><Sparkles className="h-4 w-4 text-brand-green" /> Sektör Paketleri (Hazır Kurulum)</p>
            <p className="mt-0.5 text-[11px] text-slate-500">İşletme tipinizi seçin — o sektöre ait eklenti modülleri tek tıkla ayarlar. Gruplar uzun listeyi düzenli tutar; başlığa tıklayıp açın.</p>
          </div>
          <div className="relative w-full sm:w-56">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input data-testid="package-search" value={pkgQuery} onChange={(e) => setPkgQuery(e.target.value)} placeholder="Paket ara…" className="h-8 pl-8" />
          </div>
        </div>
        <div className="mt-3 space-y-2">
          {pkgGroups.map((grp, gi) => {
            const opened = isPkgOpen(grp.group, gi);
            const isStar = grp.group === "⭐ Özel";
            return (
              <div key={grp.group} data-testid={`package-group-${grp.group}`} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <button type="button" data-testid={`package-group-toggle-${grp.group}`} onClick={() => setOpenPkg((o) => ({ ...o, [grp.group]: !opened }))}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-slate-50">
                  <span className="shrink-0 text-slate-400">{opened ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</span>
                  {isStar ? <Sparkles className="h-4 w-4 text-amber-500" /> : null}
                  <span className="flex-1 text-sm font-bold text-slate-700">{grp.group}</span>
                  <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">{grp.items.length}</span>
                </button>
                {opened && (
                  <div className="grid gap-2 border-t border-slate-100 p-3 sm:grid-cols-2 lg:grid-cols-3">
                    {grp.items.map((s) => {
                      const Icon = sectorIcon(s.icon);
                      const busy = savingKey === `pkg:${s.code}`;
                      const isFull = s.code === "full";
                      const modCount = (s.modules || []).filter((r) => GATED.has(r)).length;
                      return (
                        <button key={s.code} type="button" data-testid={`package-${s.code}`} onClick={() => openPreview(s)} disabled={!!savingKey}
                          className={`flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors disabled:opacity-50 ${isFull ? "border-amber-300 bg-amber-50/60 hover:border-amber-400" : "border-slate-200 bg-white hover:border-brand-green/50 hover:bg-emerald-50/50"}`}>
                          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white" style={{ backgroundColor: s.color || "#059669" }}>
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-bold text-slate-800">{s.name}</span>
                            <span className="block text-[11px] leading-snug text-slate-400">{isFull ? "tüm modüller + özellikler" : `${modCount} sektör modülü`}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {pkgGroups.length === 0 && <p className="py-2 text-center text-xs text-slate-400">Eşleşen paket yok.</p>}
        </div>
      </div>

      {groups.map((g) => {
        const open = openGroups[g.key];
        const flags = g.flags.filter((f) => f.key !== "flow.require_order_approval" && f.key !== "flow.multi_step_approval");
        if (flags.length === 0) return null;
        const onCount = flags.filter((f) => eff[f.key] === true).length;
        return (
          <div key={g.key} data-testid={`feature-group-${g.key}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <button type="button" data-testid={`feature-group-toggle-${g.key}`} onClick={() => setOpenGroups((s) => ({ ...s, [g.key]: !s[g.key] }))}
              className="flex w-full items-center gap-2 p-4 text-left transition-colors hover:bg-slate-50">
              <span className="shrink-0 text-slate-400">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</span>
              <span className="min-w-0 flex-1">
                <span className="block font-heading text-sm font-bold text-slate-800">{g.title}</span>
                {g.help && <span className="mt-0.5 block text-[11px] text-slate-400">{g.help}</span>}
              </span>
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">{onCount}/{flags.length} açık</span>
            </button>
            {open && (
              <div className="border-t border-slate-100 p-4">
                <div className="grid gap-2 sm:grid-cols-2">
                  {flags.map((f) => {
                    const on = eff[f.key] === true;
                    return (
                      <label key={f.key} data-testid={`feature-row-${f.key}`}
                        className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 ring-1 ring-inset transition-colors ${on ? "bg-emerald-50/50 ring-emerald-200" : "bg-slate-50 ring-slate-200"}`}>
                        <span className="min-w-0 flex-1 text-sm font-medium text-slate-700">{f.label}</span>
                        {savingKey === f.key
                          ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-400" />
                          : <Switch data-testid={`feature-switch-${f.key}`} checked={on} onCheckedChange={(v) => toggle(f.key, v)} />}
                      </label>
                    );
                  })}
                </div>
                {g.key === "flow" && <p className="mt-3 text-[11px] text-slate-400">🔗 Onay zinciri (aç/kapa · çok kademe · tutar eşiği) tek yerden yönetilir: <b>Onaylar → Onay Ayarları</b>.</p>}
              </div>
            )}
          </div>
        );
      })}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="package-preview-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>"{preview?.sector?.name}" paketi uygulanacak</DialogTitle>
            <DialogDescription>
              {preview?.isFull
                ? "TÜM eklenti modüller, yetenekler ve iş akışı adımları AÇILACAK. Yetkileri sonra tek tek kısabilirsiniz."
                : `${preview?.modCount || 0} sektör modülü bu sektöre göre otomatik açılıp kapanacak (çekirdek modüller etkilenmez).`}
            </DialogDescription>
          </DialogHeader>
          {!preview?.isFull && (preview?.rules?.length ? (
            <div className="space-y-2">
              <p className="text-xs font-bold text-slate-600">Bu sektörde otomatik ayarlanacak POS kuralları:</p>
              <ul className="space-y-1.5">
                {preview.rules.map((r) => (
                  <li key={r.key} data-testid={`preview-rule-${r.key}`} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm ring-1 ring-inset ring-slate-200">
                    <span className="min-w-0 flex-1 text-slate-700">{r.label}</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${r.to ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                      {r.to ? "AÇILACAK" : "KAPALI"}{r.changed ? " •" : ""}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-slate-400">• işaretli kurallar mevcut durumdan değişecek. Diğer yetenek/zorunluluk ayarlarınız korunur; sonra tek tek değiştirebilirsiniz.</p>
            </div>
          ) : <p className="text-xs text-slate-400">Bu sektör için özel bir POS kuralı önerisi yok; yalnız modüller ayarlanacak.</p>)}
          <DialogFooter>
            <Button variant="outline" data-testid="preview-cancel" onClick={() => setPreview(null)}>Vazgeç</Button>
            <Button data-testid="preview-confirm" onClick={confirmApply} disabled={!!savingKey}>
              {savingKey ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Uygula
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
